import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Card, CircleButton, withStyles } from 'vopay';
import { Title, Button } from 'UI';
import type { OpenDialogProps } from '@/providers/Dialog.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import usePostSendOnboardingEmail from '@/hooks/useApi/usePostSendOnboardingEmail';
import SendIcon from './Send.svg';
import styles from './SendEmailDialog.module.scss';

const cn = withStyles(styles);

export type SendEmailDialogResult = { status: 'success' } | { status: 'error'; errorMessage: string };

type SendEmailDialogProps = OpenDialogProps<SendEmailDialogResult> & {
  email: string;
  name: string;
  link: string;
  accountId: string;
  bearer: string;
};

const SendEmailDialog: FunctionComponent<SendEmailDialogProps> = ({
  onConfirm,
  onCancel,
  email,
  name,
  link,
  accountId,
  bearer,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.OnboardingEmail' });
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [emailResponse, sendOnboardingEmail] = usePostSendOnboardingEmail({ bearer });

  useEffect(() => {
    if (!hasSubmitted) return;
    const { Success, ErrorMessage } = emailResponse;
    if (!Success && !ErrorMessage) return;

    setHasSubmitted(false);

    if (Success) {
      onConfirm({ status: 'success' });
    } else {
      onConfirm({ status: 'error', errorMessage: ErrorMessage });
    }
  }, [emailResponse]);

  const handleSend = () => {
    setHasSubmitted(true);
    sendOnboardingEmail({ Name: name, Email: email, Link: link, VopayAccountID: accountId });
  };

  return (
    <Card hoverEffect={false} className={cn('dialog')}>
      <div className={cn('closeButton')}>
        <CircleButton colorScheme="neutral" icon="close" size="large" onClick={onCancel} />
      </div>
      <img className={cn('image')} src={SendIcon} alt={t('sendIconAlt', { defaultValue: 'Send Email' })} />
      <div className={cn('header')}>
        <Title level="3">{t('confirmTitle')}</Title>
        <BodyText className={cn('greyText')}>{t('confirmBody', { email })}</BodyText>
      </div>
      <div className={cn('buttons')}>
        <Button colorScheme="secondary" size="small" onClick={onCancel}>
          {t('cancelButton')}
        </Button>
        <Button
          id="send-email-confirm"
          colorScheme="primary"
          size="small"
          disabled={isRoleViewOnly}
          onClick={handleSend}
        >
          {t('sendButton')}
        </Button>
      </div>
    </Card>
  );
};

export default SendEmailDialog;
